import * as htmlToImage from 'html-to-image';
import jsPDF from 'jspdf';

class CoveragesHelper {
	calculateLimits(dtoBuilding, policyType) {
		const result = [];
		if (policyType !== 'HO4') {
			result.push(
				{
					label: 'A - Dwelling',
					value: this.formatNumber(dtoBuilding.CovALimit.toString()),
				}
			);
		}

		if (policyType !== 'HO4' && policyType !== 'HO6') {
			result.push(
				{
					label: 'B - Other Structures',
					value: this.formatNumber(dtoBuilding.CovBLimit?.toString()),
				}
			);
		}

		if (
			policyType !== 'DF1' &&
			policyType !== 'DF3-DO' &&
			policyType !== 'DF3-DL'
		) {
			result.push({
				label: 'C - Personal Property',
				value: this.formatNumber(dtoBuilding.CovCLimit.toString()),
			});
			result.push({
				label: 'D - Loss of Use',
				value: this.formatNumber(dtoBuilding.CovDLimit.toString()),
			});
		} else {
			const calculated = +dtoBuilding.CovALimit * 0.25;
			result.push({
				label: 'C - Personal Property',
				value: this.formatNumber(calculated),
			});
		}

		const liabLimit = dtoBuilding.CovELimit || dtoBuilding.CovLLimit || dtoBuilding.CovPLimit;
		if (liabLimit)
			result.push({
				label: 'E - Personal Liability',
				value: this.formatNumber(liabLimit?.toString()),
			});

		const medLimit = dtoBuilding.CovFLimit || dtoBuilding.CovMLimit;
		if (medLimit)
			result.push({
				label: 'F - Medical Payments',
				value: this.formatNumber(medLimit?.toString()),
			});

		return result;
	}

	calculatePremiumAmountAndFees(premiumAmount, dtoFee) {
		const fees = dtoFee
			?.filter((item) => item.Status === 'Active')
			.map((item) => item.FinalAmt)
			.reduce((acc, curr) => {
				return acc + Number(curr);
			}, 0);

		return !!fees ? premiumAmount + fees : premiumAmount;
	}

	calculateDeductibles(dtoBuilding, quoteType) {
		const result = [
			{
				label: 'Non-Hurricane Deductible',
				value: this.formatNumber(dtoBuilding.AllPerilDed?.toString()),
			},
		];
		switch (quoteType) {
			case 'HO3':
			case 'HO5':
			case 'DF3-DO':
				result.push({
					label: 'Hurricane Deductible',
					value: this.numberToPercents(
						dtoBuilding.HurricaneDed?.toString()
					),
				});
				break;
			case 'HO4':
			case 'HO6':
			case 'DF1':
			case 'DF3-DL':
				result.push({
					label: 'Hurricane Deductible',
					value: this.formatNumber(
						dtoBuilding.HurricaneDed?.toString()
					),
				});
				break;
		}

		if (dtoBuilding.WaterDamageCov !== undefined) {
			result.push({
				label: 'Water Damage Deductible',
				value: dtoBuilding.WaterDamageCov,
			});
		}
		return result;
	}

	calculateAdditionalCoverages(dtoBuilding, policyType) {
		const additionalCoveragesArray = [];

		if (
			dtoBuilding.OrdinanceLawCov !== undefined &&
			(policyType !== 'HO4' || policyType !== 'DF1')
		) {
			additionalCoveragesArray.push({
				label: 'Ordinance or Law Coverage',
				value: this.numberToPercents(dtoBuilding.OrdinanceLawCov),
			});
		}

		if (
			dtoBuilding.LimitedSECCov !== '0' &&
			dtoBuilding.LimitedSECCov !== undefined
		) {
			additionalCoveragesArray.push({
				label: 'Limited Screened Enclosure and Carport Coverage',
				value: dtoBuilding.LimitedSECCov,
			});
		}

		if (
			dtoBuilding.IncreasedReplacementCostDwelling !== 'No' &&
			dtoBuilding.IncreasedReplacementCostDwelling !== undefined
		) {
			additionalCoveragesArray.push({
				label: 'Increased Replacement Cost on Dwelling',
				value: dtoBuilding.IncreasedReplacementCostDwelling,
			});
		}

		if (
			dtoBuilding.SpecialPersonalPropCov !== 'No' &&
			dtoBuilding.SpecialPersonalPropCov !== undefined
		) {
			additionalCoveragesArray.push({
				label: 'Special Personal Property Coverage',
				value: dtoBuilding.SpecialPersonalPropCov,
			});
		}

		if (
			dtoBuilding.OptionalSinkholeLoss !== 'Excluded' &&
			dtoBuilding.OptionalSinkholeLoss !== undefined
		) {
			additionalCoveragesArray.push({
				label: 'Optional Sinkhole Loss Coverage',
				value: dtoBuilding.OptionalSinkholeLoss,
			});
		}

		if (
			dtoBuilding.TheftFraudExpenseCov !== 'No' &&
			dtoBuilding.TheftFraudExpenseCov !== undefined
		) {
			additionalCoveragesArray.push({
				label: 'Identity Theft Expense Coverage',
				value: dtoBuilding.TheftFraudExpenseCov,
			});
		}

		if (dtoBuilding.LimitedFMWDBCov !== undefined && policyType !== 'HO4') {
			const [value1, value2] = dtoBuilding.LimitedFMWDBCov.split('/').map(
				(num) => this.formatNumber(num)
			);
			additionalCoveragesArray.push({
				label: 'Limited Fungi, Mold, Wet or Dry Rot, or Bacteria Coverage',
				value: `${value1}/${value2}`,
			});
		}

		if (
			dtoBuilding.PersonalInjuryCov !== 'No' &&
			dtoBuilding.PersonalInjuryCov !== undefined
		) {
			additionalCoveragesArray.push({
				label: 'Personal Injury Coverage',
				value: dtoBuilding.PersonalInjuryCov,
			});
		}

		if (
			dtoBuilding.PersonalPropertyReplacementCost !== 'No' &&
			dtoBuilding.PersonalPropertyReplacementCost !== undefined
		) {
			additionalCoveragesArray.push({
				label: 'Personal Property Replacement Cost Coverages',
				value: dtoBuilding.PersonalPropertyReplacementCost,
			});
		}

		if (
			dtoBuilding.CompEquipCov !== '0' &&
			dtoBuilding.CompEquipCov !== undefined
		) {
			additionalCoveragesArray.push({
				label: 'Computer Equipment Coverage',
				value: dtoBuilding.CompEquipCov,
			});
		}

		if (
			dtoBuilding.EquipServiceEnhancement !== 'None' &&
			dtoBuilding.EquipServiceEnhancement !== undefined
		) {
			additionalCoveragesArray.push({
				label: 'Equipment Breakdown and Service Line Enhancement',
				value: dtoBuilding.EquipServiceEnhancement,
			});
		}

		return additionalCoveragesArray;
	}

	formatNumber(number) {
		if (number != null) {
			const formatter = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
				maximumFractionDigits: 0,
			});

			return formatter.format(number);
		}
	}

	calculateMonthlyPremium(number) {
		if (number != null) {
			return Math.round(number / 12);
		}
	}

	numberToPercents(number) {
		if (number != null) {
			const formatter = new Intl.NumberFormat('en-US', {
				style: 'percent',
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			});

			return formatter.format(number);
		}
	}

	printScreen() {
		const content = document.getElementById('content');
		const clone = content.cloneNode(true);
		clone.style.margin = '0';
		clone.style.padding = '10px';
		clone.style.boxShadow = 'none';

		const container = document.createElement('div');
		container.appendChild(clone);
		document.body.appendChild(container);

		htmlToImage.toPng(clone, { quality: 2 }).then((dataUrl) => {
			const pdf = new jsPDF('p', 'mm', 'a4');

			const a4Width = 210;
			const a4Height = 297;

			const img = new Image();
			img.src = dataUrl;
			img.onload = function () {
				const contentWidth = img.width;
				const contentHeight = img.height;

				const scale = Math.min(
					a4Width / contentWidth,
					a4Height / contentHeight
				);
				const imgWidth = contentWidth * scale;
				const imgHeight = contentHeight * scale;

				const xOffset = (a4Width - imgWidth) / 2;
				const yOffset = 0;

				pdf.addImage(
					dataUrl,
					'PNG',
					xOffset,
					yOffset,
					imgWidth,
					imgHeight
				);
				pdf.save(
					`Coverage_${localStorage.getItem('quote_number')}.pdf`
				);

				document.body.removeChild(container);
			};
		});
	}
}

const coveragesHelper = new CoveragesHelper();
export default coveragesHelper;
